import { PlusOutlined } from '@ant-design/icons'
import { Button, Form, Input, Table, TableColumnsType } from 'antd'
import clsx from 'clsx'
import { StakingTokenConfigItem, StakingTokenConfigPoolType, StakingTokenType } from 'Modules'
import { useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ProtectedButton from 'component/button/protected-button'
import LinkAddress from 'component/link/link-address'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_OPTIONS,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_MAX,
} from 'constants/pagination'
import { StakingTokenTypeLabel } from 'constants/staking'
import { logError } from 'utils/logger'
import { getObjectToValue } from 'utils/object'

import ExpandedConfigList from './expanded-config-list'
import ModalConfig, { ModalConfigMode } from './modal-config'
import ModalDelete from './modal-delete'

import 'pages/staking-token/style.scss'
import './style.scss'

const StakingTokenConfigPage = () => {
  const [filterForm] = Form.useForm()

  const [pools, setPools] = useState<StakingTokenConfigPoolType[]>([])
  const [totalPools, setTotalPools] = useState(0)
  const [loading, setLoading] = useState(false)

  const [allPools, setAllPools] = useState<StakingTokenType[]>([])
  const [createOtherOpen, setCreateOtherOpen] = useState(false)

  const [filter, setFilter] = useState<{ name?: string }>({})
  const [pagination, setPagination] = useState({
    page: DEFAULT_PAGE_INDEX,
    limit: DEFAULT_PAGE_SIZE,
  })

  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<ModalConfigMode>('create')
  const [activePool, setActivePool] = useState<StakingTokenConfigPoolType | null>(null)
  const [editingConfig, setEditingConfig] = useState<StakingTokenConfigItem | null>(null)

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deletingConfig, setDeletingConfig] = useState<StakingTokenConfigItem | null>(null)

  const fetchPools = async () => {
    try {
      setLoading(true)
      const res = await AppAdmin.getListStakingTokenConfig({
        page: pagination.page,
        limit: pagination.limit,
        ...(filter.name ? { name: filter.name } : {}),
      })
      if (res?.data.success) {
        setPools(res.data.data?.pools || [])
        setTotalPools(res.data.data?.totalResults || 0)
      }
    } catch (error) {
      logError('Error: ', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPools()
  }, [pagination, filter])

  const fetchAllPools = async () => {
    try {
      const res = await AppAdmin.getListStakingToken({
        page: DEFAULT_PAGE_INDEX,
        limit: DEFAULT_PAGE_SIZE_MAX,
      })
      if (res?.data.success) {
        setAllPools(res.data.data?.results || [])
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }

  useEffect(() => {
    fetchAllPools()
  }, [])

  const handleEditConfig = (pool: StakingTokenConfigPoolType, config: StakingTokenConfigItem) => {
    setModalMode('update')
    setActivePool(pool)
    setEditingConfig(config)
    setModalOpen(true)
  }

  const handleDeleteConfig = (config: StakingTokenConfigItem) => {
    setDeletingConfig(config)
    setDeleteOpen(true)
  }

  const handleCreateOther = () => {
    setActivePool(null)
    setEditingConfig(null)
    setCreateOtherOpen(true)
  }

  const handleCreateSuccess = () => {
    fetchPools()
    fetchAllPools()
  }

  const poolColumns: TableColumnsType<StakingTokenConfigPoolType> = [
    {
      title: 'Pool Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <LinkAddress address={text} />
        </div>
      ),
    },
    {
      title: 'Period',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const objType = getObjectToValue(StakingTokenTypeLabel, type)
        return (
          <span className={clsx({ [`${objType?.labelClassName}`]: true })}>
            {objType?.label || ''}
          </span>
        )
      },
    },
    {
      title: 'Config Count',
      key: 'configCount',
      width: 140,
      render: (_, record) => record.configs?.length || 0,
    },
  ]

  const additionalItems = [BREADCRUMBS.STAKING_TOKEN_CONFIG]

  return (
    <div className="main-section staking staking-token-config">
      <Breadcrumb additionalItems={additionalItems} />
      <div className="main-title">Staking Token Config</div>

      <div className="table-row">
        <div className="main-table-row">
          <div className="table-header">
            <Form
              form={filterForm}
              layout="inline"
              onFinish={(values) => {
                setFilter({ name: values.name })
                setPagination({ ...pagination, page: DEFAULT_PAGE_INDEX })
              }}
            >
              <Form.Item label="Name" name="name">
                <Input allowClear placeholder="Enter pool name or config name" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Search
                </Button>
              </Form.Item>
            </Form>

            <ProtectedButton
              type="primary"
              icon={<PlusOutlined />}
              feature={FEATURE.SETTING}
              requireCRUD
              onClick={handleCreateOther}
            >
              Create
            </ProtectedButton>
          </div>

          <Table<StakingTokenConfigPoolType>
            rowKey="_id"
            className="main-table"
            columns={poolColumns}
            dataSource={pools}
            loading={loading}
            scroll={{ x: 1000 }}
            expandable={{
              expandedRowRender: (pool) => (
                <ExpandedConfigList
                  pool={pool}
                  onEdit={handleEditConfig}
                  onDelete={handleDeleteConfig}
                />
              ),
              expandRowByClick: true,
            }}
            pagination={
              totalPools <= 10
                ? false
                : {
                    current: pagination.page,
                    pageSize: pagination.limit,
                    pageSizeOptions: DEFAULT_PAGE_OPTIONS,
                    total: totalPools,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    onShowSizeChange(current, pageSize) {
                      setPagination({ page: current, limit: pageSize })
                    },
                    onChange(page, pageSize) {
                      setPagination({ page: page, limit: pageSize })
                    },
                  }
            }
          />
        </div>
      </div>

      <ModalConfig
        open={modalOpen}
        mode={modalMode}
        stakingTokenId={activePool?._id || ''}
        poolName={activePool?.name || ''}
        config={editingConfig}
        onClose={() => setModalOpen(false)}
        onSuccess={fetchPools}
      />

      <ModalConfig
        open={createOtherOpen}
        mode="create"
        stakingTokenId=""
        poolName=""
        poolOptions={allPools}
        onClose={() => setCreateOtherOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <ModalDelete
        open={deleteOpen}
        config={deletingConfig}
        onClose={() => setDeleteOpen(false)}
        onSuccess={fetchPools}
      />
    </div>
  )
}

export default StakingTokenConfigPage
